import React, { Component } from 'react';
import { Form } from 'semantic';
import { isNumber, isNil, set } from 'lodash-es';
import ListField from 'components/form-fields/List';
import { withTranslation } from 'react-i18next';

function checkRefType(field, definitions) {
  if (!field['$ref']) {
    return field;
  }

  const defKey = field['$ref']?.split('/').pop();
  return definitions[defKey];
}

export class JSONSchemaInputs extends Component {
  constructor(props) {
    super(props);
    this.state = { params: props.params || {} };
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.params !== prevState.params) {
      return {
        params: nextProps.params,
      };
    }
    return null;
  }

  setParamsField(path, value) {
    this.setState((prevState) => {
      const newParams = { ...prevState.params };
      set(newParams, path, value);
      this.props.onChange(newParams);
      return { params: newParams };
    });
  }

  render() {
    const { params } = this.state;
    const { schema, t } = this.props;
    const { properties, required, definitions } = schema;

    return (
      <>
        {Object.keys(properties).map((key) => {
          let label = key;
          let field = properties[key];

          // check if field is ref type
          field = checkRefType(field, definitions);

          let { type, items, maxLength, minLength, $ref } = field;

          const enumValue = field.enum;
          const value = params[key] || '';
          let Element = Form.Input;

          if (type === 'object' || $ref) {
            Element = Form.TextArea;
            label += ' (JSON)';
          }
          if (type === 'array') {
            if (items && items.type === 'string') {
              Element = ListField;
            } else {
              Element = Form.TextArea;
              label += ' (JSON)';
            }
          }
          if (type === 'string' && maxLength > 100) {
            label += ` (max ${maxLength} characters)`;
            Element = Form.TextArea;
          }
          let options = undefined;
          if (enumValue) {
            Element = Form.Select;
            options = enumValue.map((key) => {
              return {
                key,
                value: key,
                text: key,
              };
            });
          }

          // required in json schema is not the same as required in the form
          // required in json schema means that the key is required in the request but the value can be empty
          // while required in the form means that the value cannot be empty
          // so we need to check if the minLength is greater than 0 to determine if the field is required
          const isRequired =
            type === 'string'
              ? isNumber(minLength) && minLength > 0
              : required?.includes(key);
          const nonNilField =
            type === 'string' ? required?.includes(key) : false;

          return (
            <>
              <Element
                required={isRequired}
                key={key}
                label={label}
                name={key}
                value={value}
                onChange={(e, { name, value }) => {
                  this.setParamsField(name, value);
                }}
                options={options}
              />
              {nonNilField && (
                <div style={{ marginBottom: 10, marginTop: -10, fontSize: 12 }}>
                  <i>
                    {t(
                      'jsonSchemaInputs.nonNilField',
                      'Defaults to an empty value if none is provided.'
                    )}
                  </i>
                </div>
              )}
            </>
          );
        })}
      </>
    );
  }
}

export function normalize(schema, params) {
  const { properties, definitions, required } = schema;
  if (!properties) return params;
  const newParams = {};

  Object.keys(params).forEach((key) => {
    if (!properties[key]) {
      newParams[key] = params[key];
    } else {
      const field = checkRefType(properties[key], definitions);

      const { type, items, $ref } = field;
      if (type === 'integer') {
        newParams[key] = parseInt(params[key], 10);
      } else if (type === 'object' || $ref) {
        newParams[key] = JSON.parse(params[key]);
      } else if (type === 'array') {
        if (items && items.type === 'string') {
          newParams[key] = params[key];
        } else {
          newParams[key] = JSON.parse(params[key]);
        }
      } else {
        newParams[key] = params[key];
      }
    }
  });

  // if the input was never touched, set it to empty string as required by the json schema
  required?.forEach((key) => {
    if (properties[key]?.type === 'string' && isNil(newParams[key])) {
      newParams[key] = '';
    }
  });

  return newParams;
}

export default withTranslation()(JSONSchemaInputs);
